/**
 * useCoverNight — whether it is dark out, for the one thing a cover's drawing
 * still asks the sky about.
 *
 * The owner, 2026-10-26: *"drop the weather bg in shade window"* — the drawing
 * paints the device's own picture or plain glass, never a forecast, so the
 * weather read that used to supply its gradient is gone. What survives is the
 * day/night fact, which decides how that picture is shown: greyed after sunset,
 * in colour by day.
 *
 * Pure solar, no network: `useSolar` is already a singleton.
 */

import { computed, type ComputedRef } from 'vue';

import { useSolar } from '@/composables/useSolar';

/** What an unreported hour reads as: a mild day, never a black void. */
const DEFAULT_ALTITUDE_DEG = 20;

/** True after sunset — the picture behind a cover's glass is shown greyed. */
export function useCoverNight(): ComputedRef<boolean> {
  const solar = useSolar();

  return computed(() => {
    const altitude = solar.sunPosition.value?.altitude;
    const altitudeDeg = typeof altitude === 'number' && Number.isFinite(altitude) ? altitude : DEFAULT_ALTITUDE_DEG;
    // The hub's own isDay is the authority; the altitude is the fallback for a
    // hub that answers solar without it.
    return solar.isDay.value === false || altitudeDeg <= 0;
  });
}
